"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useState, ReactNode, useEffect, useRef, useCallback } from "react";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import { transformMediaUrl } from "@/lib/media-url-transformer";
import type { TourRouteMapRef } from "./TourRouteMap";
import { sanitizeCmsHtml } from "@/lib/sanitize-cms-html";

interface MapStoryPoint {
    id: string;
    lat: number;
    lng: number;
    title: string;
    description: string;
    image?: string;
    day?: number;
    /** 1-based durak sırası (tur geneli) */
    stopIndex?: number;
    dayId?: string;
    dayStopIndex?: number;
    dayStopTotal?: number;
}

interface InteractiveMapStoryProps {
    points: MapStoryPoint[];
    onPointClick?: (point: MapStoryPoint, index: number) => void;
    onStoryModeChange?: (isActive: boolean) => void;
    mapRef?: React.RefObject<TourRouteMapRef | null>;
    children?: ReactNode;
    /** Son adımda (örn. 8/8) broşür indirme alanı - genelde buton veya kısa form */
    renderLastStepExtra?: () => ReactNode;
    onOpenBrochure?: () => void;
    /** Son adımda talep formu modalını açacak callback */
    onOpenRequestForm?: () => void;
    /** Harita marker senkronu (kontrollü mod) */
    activeIndex?: number;
    onActiveIndexChange?: (index: number) => void;
}

export default function InteractiveMapStory({ 
    points,
    onPointClick,
    onStoryModeChange,
    mapRef,
    children,
    renderLastStepExtra,
    onOpenBrochure,
    onOpenRequestForm,
    activeIndex: controlledActiveIndex,
    onActiveIndexChange,
}: InteractiveMapStoryProps) {
    const { getVariable } = useLanguageVariables();
    const [isStoryModeActive, setIsStoryModeActive] = useState(false);
    const [internalStoryIndex, setInternalStoryIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const [mobileSheetExpanded, setMobileSheetExpanded] = useState(false);
    const prevStoryModeRef = useRef(false);

    const isControlled = controlledActiveIndex !== undefined;
    const currentStoryIndex = isControlled ? controlledActiveIndex : internalStoryIndex;

    const setStoryIndex = useCallback((index: number) => {
        if (isControlled) {
            onActiveIndexChange?.(index);
        } else {
            setInternalStoryIndex(index);
        }
    }, [isControlled, onActiveIndexChange]);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const currentPoint = points[currentStoryIndex];

    // Haritanın geocode ettiği koordinatları kullan (adres-only noktalar için); yoksa point.lat/lng
    const getCoordsForIndex = useCallback((index: number): { lat: number; lng: number } | null => {
        const resolved = mapRef?.current?.getResolvedCoordinates?.();
        if (resolved?.length && resolved[index] != null) {
            return { lat: resolved[index].lat, lng: resolved[index].lng };
        }
        const p = points[index];
        return p && typeof p.lat === 'number' && typeof p.lng === 'number' ? { lat: p.lat, lng: p.lng } : null;
    }, [points]);

    // Hikaye modu ilk açıldığında haritayı mevcut noktaya odakla (zoom yapmıyoruz, araba kayma efekti görünsün)
    useEffect(() => {
        const justOpened = isStoryModeActive && !prevStoryModeRef.current;
        prevStoryModeRef.current = isStoryModeActive;
        if (!justOpened || !mapRef?.current) return;
        const coords = getCoordsForIndex(currentStoryIndex);
        if (coords) {
            mapRef.current.centerToLocation(coords.lat, coords.lng);
        }
    }, [isStoryModeActive, currentStoryIndex, getCoordsForIndex]);

    // Harita marker tıklaması ile kontrollü index değişince haritayı odakla
    useEffect(() => {
        if (!isStoryModeActive || !isControlled || !mapRef?.current) return;
        const coords = getCoordsForIndex(currentStoryIndex);
        if (coords) {
            mapRef.current.centerToLocation(coords.lat, coords.lng);
        }
    }, [isStoryModeActive, isControlled, currentStoryIndex, getCoordsForIndex, mapRef]);

    const handleStoryModeToggle = () => {
        const newState = !isStoryModeActive;
        if (!newState && points.length > 0) {
            setStoryIndex(0);
            setMobileSheetExpanded(false);
        }
        setIsStoryModeActive(newState);
        onStoryModeChange?.(newState);
    };

    const navigateToIndex = useCallback((nextIndex: number, fromIndex: number) => {
        const nextPoint = points[nextIndex];
        if (!nextPoint) return;
        setStoryIndex(nextIndex);
        onPointClick?.(nextPoint, nextIndex);

        const toCoords = getCoordsForIndex(nextIndex);
        if (mapRef?.current && toCoords) {
            const map = mapRef.current;
            map.centerToLocation(toCoords.lat, toCoords.lng);
            if (fromIndex !== nextIndex) {
                if (map.animateCarBetweenIndices) {
                    map.animateCarBetweenIndices(fromIndex, nextIndex);
                } else {
                    const fromCoords = getCoordsForIndex(fromIndex);
                    if (fromCoords) {
                        map.animateCarToLocation(fromCoords, toCoords);
                    }
                }
            }
        }
    }, [points, setStoryIndex, onPointClick, getCoordsForIndex, mapRef]);

    const nextStory = () => {
        if (currentStoryIndex < points.length - 1) {
            navigateToIndex(currentStoryIndex + 1, currentStoryIndex);
        }
    };

    const prevStory = () => {
        if (currentStoryIndex > 0) {
            navigateToIndex(currentStoryIndex - 1, currentStoryIndex);
        }
    };

    const goToStory = (index: number) => {
        if (index < 0 || index >= points.length || index === currentStoryIndex) return;
        navigateToIndex(index, currentStoryIndex);
    };

    const isLastStep = points.length > 0 && currentStoryIndex === points.length - 1;

    const formatStopChipLabel = (point: MapStoryPoint, index: number) => {
        if (point.dayStopIndex != null && point.dayStopTotal != null && point.dayStopTotal > 1) {
            return `${point.day ?? index + 1}.${point.dayStopIndex}`;
        }
        return String(point.stopIndex ?? index + 1);
    };

    const storyPanelContent = currentPoint ? (
        <div className={isMobile ? 'p-4' : 'p-6'}>
            {currentPoint.image && (
                <motion.div
                    key={currentStoryIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={isMobile ? 'mb-4 -mx-4' : 'mb-6 -mx-6 -mt-6'}
                >
                    <img
                        src={transformMediaUrl(currentPoint.image, null) || currentPoint.image}
                        alt={currentPoint.title}
                        className={`w-full object-cover ${isMobile ? 'h-36' : 'h-48'}`}
                    />
                </motion.div>
            )}

            <motion.div
                key={`header-${currentStoryIndex}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4"
            >
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                    {currentPoint.day && (
                        <span className="bg-alsero-first text-white text-sm font-semibold px-3 py-1 rounded-full">
                            {getVariable('map_day', 'Day')} {currentPoint.day}
                        </span>
                    )}
                    {currentPoint.dayStopIndex != null && currentPoint.dayStopTotal != null && currentPoint.dayStopTotal > 1 && (
                        <span className="bg-gray-100 text-gray-700 text-sm font-semibold px-3 py-1 rounded-full">
                            {getVariable('map_stop', 'Stop')} {currentPoint.dayStopIndex}/{currentPoint.dayStopTotal}
                        </span>
                    )}
                    {currentPoint.stopIndex != null && (
                        <span className="text-xs text-gray-500">
                            {getVariable('map_route_stop', 'Route stop')} {currentPoint.stopIndex}/{points.length}
                        </span>
                    )}
                </div>
                <h3 className={`font-bold text-alsero-dark-gray mb-2 ${isMobile ? 'text-lg' : 'text-2xl'}`}>
                    {currentPoint.title}
                </h3>
                {currentPoint.description ? (
                    <div
                        className="text-gray-600 leading-relaxed prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(currentPoint.description) }}
                    />
                ) : (
                    <p className="text-gray-500 italic text-sm">
                        {getVariable('map_description_not_added', 'Description not added yet.')}
                    </p>
                )}
            </motion.div>

            <div className="mb-4 pt-3 border-t border-gray-200">
                <div className="flex flex-wrap items-center justify-center gap-2 max-h-28 overflow-y-auto">
                    {points.map((point, index) => (
                        <button
                            type="button"
                            key={point.id}
                            onClick={() => goToStory(index)}
                            className={`min-w-[2rem] h-8 px-2 rounded-full text-xs font-bold transition-all ${
                                index === currentStoryIndex
                                    ? 'bg-alsero-first text-white shadow-md scale-105'
                                    : index === 0
                                      ? 'bg-green-600 text-white hover:bg-green-700'
                                      : index === points.length - 1
                                        ? 'bg-slate-800 text-white hover:bg-slate-900'
                                        : 'bg-white border-2 border-alsero-first text-alsero-first hover:bg-red-50'
                            }`}
                            aria-label={`${getVariable('map_stop', 'Stop')} ${formatStopChipLabel(point, index)}`}
                            aria-current={index === currentStoryIndex ? 'step' : undefined}
                        >
                            {formatStopChipLabel(point, index)}
                        </button>
                    ))}
                </div>
            </div>

            {isLastStep && (renderLastStepExtra || onOpenBrochure || onOpenRequestForm) && (
                <div className="mb-4 pt-3 border-t border-gray-200 space-y-3">
                    {renderLastStepExtra?.() ?? (
                        <>
                            {onOpenBrochure && (
                                <button
                                    type="button"
                                    onClick={onOpenBrochure}
                                    className="w-full px-4 py-3 bg-alsero-first text-white font-semibold rounded-lg hover:bg-alsero-first/90 transition-colors flex items-center justify-center gap-2"
                                >
                                    {getVariable('map_download_brochure', 'Download brochure')}
                                </button>
                            )}
                            {onOpenRequestForm && (
                                <button
                                    type="button"
                                    onClick={onOpenRequestForm}
                                    className="w-full px-4 py-3 bg-gray-100 text-gray-800 font-semibold rounded-lg hover:bg-gray-200 transition-colors border border-gray-200"
                                >
                                    {getVariable('request_form.title_heading', 'Request form')}
                                </button>
                            )}
                        </>
                    )}
                </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-200">
                <button
                    type="button"
                    onClick={prevStory}
                    disabled={currentStoryIndex === 0}
                    className="flex-1 px-4 py-2.5 bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 font-medium text-gray-700"
                >
                    {getVariable('map_previous', 'Previous')}
                </button>
                <button
                    type="button"
                    onClick={nextStory}
                    disabled={currentStoryIndex === points.length - 1}
                    className="flex-1 px-4 py-2.5 bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 font-medium text-gray-700"
                >
                    {getVariable('map_next', 'Next')}
                </button>
            </div>
        </div>
    ) : null;

    return (
        <div className="relative w-full h-full">
            {/* Map Content - Harita tamamen görünür */}
            <div className="relative w-full h-full z-0">
                {children}
            </div>

            {/* Story Mode Button - Harita üzerinde */}
            <motion.button
                type="button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleStoryModeToggle}
                className={`absolute top-4 right-4 z-20 px-4 py-2 rounded-lg font-semibold shadow-lg flex items-center gap-2 transition-colors ${
                    isStoryModeActive 
                        ? 'bg-gray-700 text-white hover:bg-gray-600' 
                        : 'bg-alsero-first text-white hover:bg-alsero-first/90'
                }`}
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                {isStoryModeActive ? getVariable('map_story_mode_close', 'Close Story Mode') : getVariable('map_story_mode', 'Story Mode')}
            </motion.button>

            {/* Story Sidebar - Sağ tarafta slide-in */}
            <AnimatePresence>
                {isStoryModeActive && currentPoint && points.length > 0 && (
                    <>
                        {/* Backdrop - Hafif koyu arka plan (opsiyonel, haritayı kapatmaz) */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 bg-black/20 z-10 pointer-events-none"
                        />

                        {/* Desktop: sağ panel / Mobil: bottom sheet */}
                        <motion.div
                            initial={isMobile ? { y: '100%' } : { x: '100%' }}
                            animate={{ x: 0, y: 0 }}
                            exit={isMobile ? { y: '100%' } : { x: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            className={
                                isMobile
                                    ? `absolute bottom-0 left-0 right-0 z-30 bg-white shadow-2xl border-t border-neutral-200/80 rounded-t-2xl overflow-hidden flex flex-col ${
                                          mobileSheetExpanded ? 'max-h-[58vh]' : 'max-h-[7.5rem]'
                                      }`
                                    : 'absolute md:top-0 bottom-0 right-0 h-full w-96 bg-white shadow-2xl border border-neutral-200/80 z-30 overflow-y-auto'
                            }
                        >
                            <div className={`sticky top-0 bg-white border-b border-gray-200 flex items-center justify-between z-10 shadow-sm ${isMobile ? 'px-4 py-2' : 'p-4'}`}>
                                <h3 className="text-lg font-bold text-alsero-dark-gray">
                                    {getVariable('map_tour_story', 'Tour Story')}
                                </h3>
                                <div className="flex items-center gap-2">
                                    {isMobile && (
                                        <button
                                            type="button"
                                            onClick={() => setMobileSheetExpanded((v) => !v)}
                                            className="text-gray-500 hover:text-gray-700 p-1 rounded"
                                            aria-expanded={mobileSheetExpanded}
                                            aria-label={mobileSheetExpanded ? getVariable('map_collapse', 'Collapse') : getVariable('map_expand', 'Expand')}
                                        >
                                            <svg className={`w-5 h-5 transition-transform ${mobileSheetExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                            </svg>
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsStoryModeActive(false);
                                            setMobileSheetExpanded(false);
                                            onStoryModeChange?.(false);
                                        }}
                                        className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded"
                                        aria-label={getVariable('map_close', 'Close')}
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {isMobile && !mobileSheetExpanded && currentPoint && (
                                <button
                                    type="button"
                                    className="px-4 pb-3 text-left w-full"
                                    onClick={() => setMobileSheetExpanded(true)}
                                >
                                    <p className="text-xs text-gray-500 mb-1">
                                        {getVariable('map_stop', 'Stop')} {formatStopChipLabel(currentPoint, currentStoryIndex)} · {currentStoryIndex + 1}/{points.length}
                                    </p>
                                    <p className="font-semibold text-alsero-dark-gray line-clamp-1">{currentPoint.title}</p>
                                </button>
                            )}

                            <div className={isMobile ? (mobileSheetExpanded ? 'overflow-y-auto flex-1' : 'hidden') : undefined}>
                                {storyPanelContent}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}
